import { Button, Paper, Skeleton, Stack } from '@mui/material';
import { getSelectorAttribute, invoicesOverviewPageSelectors } from '@vy/app-selectors';
import { VyIcon } from '@vy/ui-design-system';
import { TABLE_NAMES } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import React, { Suspense } from 'react';

import type { useInvoicesFilters } from '../hooks/use-invoices-filters';
import { InvoicesTable } from '../InvoicesPage/invoices-page-components/InvoicesTable';
import { InvoicesActionsMenu } from '../InvoicesPage/invoices-page-components/invoices-actions-menu/InvoicesActionsMenu';
import { SyncInvoicesAsSalesOrderDialog } from '../InvoicesPage/invoices-page-components/SyncInvoicesAsSalesOrderDialog/SyncInvoicesAsSalesOrderDialog';
import { SyncInvoicesDialog } from '../InvoicesPage/invoices-page-components/SyncInvoicesDialog/SyncInvoicesDialog';
import type {
  InvoicesActions,
  InvoicesDisabledStates,
  InvoicesPageData,
  InvoicesSyncDialogs,
  InvoicesTableConfig,
} from '../types/invoices-page-types';

type InvoicesPageContentProps = {
  // Data - all invoice data and loading states
  data: InvoicesPageData;

  // Table configuration - table ref, selection, pagination
  tableConfig: InvoicesTableConfig;

  // Search & Filters
  filters?: ReturnType<typeof useInvoicesFilters>;

  // Actions - all bulk action callbacks
  actions: InvoicesActions;

  // Sync dialogs - both NetSuite sync types grouped
  syncDialogs: InvoicesSyncDialogs;

  // Disabled states - feature flags
  disabledStates: InvoicesDisabledStates;

  // Optional extra actions rendered alongside the bulk Actions button
  additionalActions?: React.ReactNode;

  // Optional "New invoice" handler — when provided, adds a top entry to the Actions menu
  // and keeps the Actions button always clickable.
  onCreateInvoice?: () => void;

  // Hide the download button in the table toolbar
  hideDownload?: boolean;

  // Disable column sorting in the table
  disableSorting?: boolean;
};

export const InvoicesPageContent: React.FC<InvoicesPageContentProps> = ({
  data,
  tableConfig,
  filters,
  actions,
  syncDialogs,
  disabledStates,
  additionalActions,
  onCreateInvoice,
  hideDownload,
  disableSorting,
}) => {
  const isActionsButtonAlwaysEnabled = isNotNullOrUndefined(onCreateInvoice);
  const actionsButtonDisabled = isActionsButtonAlwaysEnabled
    ? false
    : disabledStates.actionsDisabled;
  const {
    invoices,
    invoicesCount,
    selectedInvoices,
    loadingInvoices,
    invoicesHighlightsSummary,
    connectedIntegrations,
    exchangeRates,
    availableCustomFieldNames,
    hasUsageEventsReportConfiguration,
  } = data;

  const { apiRef, onInvoicesSelect, page, pageSize, onPageChange, onPageSizeChange } = tableConfig;

  return (
    <Suspense
      fallback={
        <>
          <Skeleton
            variant="rectangular"
            height={68}
            style={{
              borderRadius: '12px',
            }}
          />
          <Skeleton variant="rectangular" height={640} style={{ marginTop: 22, borderRadius: '12px' }} />
        </>
      }
    >
      <Stack sx={{
        height: "100%"
      }}>
        <Paper
          elevation={1}
          sx={{
            height: '100%',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...(disableSorting && {
              '& .MuiDataGrid-sortIcon': { display: 'none' },
              '& .MuiDataGrid-columnHeader--sortable .MuiDataGrid-iconButtonContainer': { display: 'none' },
            }),
          }}
        >
          <InvoicesTable
            apiRef={apiRef}
            tableName={TABLE_NAMES.INVOICES}
            filters={filters}
            items={invoices}
            itemsCount={invoicesCount}
            loadingItems={loadingInvoices}
            onInvoicesSelect={onInvoicesSelect}
            exchangeRates={exchangeRates}
            page={page}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onDownload={hideDownload ? undefined : actions.onExportSelectedInvoices}
            invoicesHighlightsSummary={invoicesHighlightsSummary}
            availableCustomFieldNames={availableCustomFieldNames}
            actions={
              <Stack
                direction="row"
                sx={{
                  gap: "8px",
                  alignItems: "center"
                }}>
                {additionalActions}
                <InvoicesActionsMenu
                  invoices={selectedInvoices}
                  onApproveInvoices={actions.onApproveInvoices}
                  onDownloadAsPdf={actions.onDownloadAsPdf}
                  onDownloadAsPdfStatement={actions.onDownloadAsPdfStatement}
                  onDownloadUsageEventsReport={actions.onDownloadUsageEventsReport}
                  hasUsageEventsReportConfiguration={hasUsageEventsReportConfiguration}
                  onUpdateInvoicesBillingStatus={actions.onUpdateInvoicesBillingStatus}
                  onSyncToStripe={actions.onSyncToStripe}
                  onSyncToNetSuite={syncDialogs.invoice.onOpen}
                  onSyncToNetSuiteSalesOrder={syncDialogs.salesOrder.onOpen}
                  onSyncToQuickBooks={actions.onSyncToQuickBooks}
                  onSyncToMorning={actions.onSyncToMorning}
                  onSyncToMarketplace={actions.onSyncToMarketplace}
                  onSyncToXero={actions.onSyncToXero}
                  onSyncToPaddle={actions.onSyncToPaddle}
                  onZenModeClick={actions.onZenModeClick}
                  syncToNetSuiteDisabled={disabledStates.syncToNetSuiteDisabled}
                  connectedIntegrations={connectedIntegrations}
                  disablePaidMark
                  onCreateInvoice={onCreateInvoice}
                  contractName={invoices.at(0)?.contractName ?? ''}
                >
                  <Button
                    {...getSelectorAttribute(invoicesOverviewPageSelectors.actionButton)}
                    variant="contained"
                    size="medium"
                    endIcon={
                      <VyIcon
                        icon="SmallArrow"
                        height={24}
                        style={{
                          color: actionsButtonDisabled ? '#0D1B275C' : 'white',
                        }}
                      />
                    }
                    disabled={actionsButtonDisabled}
                    type="submit"
                  >
                    Actions
                  </Button>
                </InvoicesActionsMenu>
              </Stack>
            }
          />
        </Paper>

        <SyncInvoicesDialog
          open={syncDialogs.invoice.open}
          onClose={syncDialogs.invoice.onClose}
          invoices={syncDialogs.invoice.filteredApprovedInvoices ?? []}
          onSyncInvoices={syncDialogs.invoice.onSync}
        />

        <SyncInvoicesAsSalesOrderDialog
          open={syncDialogs.salesOrder.open}
          onClose={syncDialogs.salesOrder.onClose}
          invoices={syncDialogs.invoice.filteredApprovedInvoices ?? []}
          onSyncInvoices={syncDialogs.salesOrder.onSync}
        />
      </Stack>
    </Suspense>
  );
};
